import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useVehiculos,
  useCrearVehiculo,
  useActualizarVehiculo,
  useEliminarVehiculo
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, estadoColumn } from '../../components/admin/crud';
import { codigoField, estadoField } from '../../components/admin/crud/CrudForm';
import { z } from 'zod';

import type { Vehiculo } from '../../types';
import type { CrearVehiculo } from '../../services/referenciasService';

const patenteField = {
  name: 'patente',
  label: 'Patente',
  type: 'text' as const,
  placeholder: 'ej. TRVB-80',
  required: true,
  validation: z.string().min(1, 'Patente es requerida').max(20, 'Patente muy larga'),
};

const tipoField = {
  name: 'tipo',
  label: 'Tipo',
  type: 'select' as const,
  options: [
    { value: 'CAMIONETA', label: 'Camioneta' },
    { value: 'CAMION', label: 'Camión' },
    { value: 'OTRO', label: 'Otro' },
  ],
};

const marcaField = {
  name: 'marca',
  label: 'Marca',
  type: 'text' as const,
  placeholder: 'ej. Toyota',
  validation: z.string().optional(),
};

const modeloField = {
  name: 'modelo',
  label: 'Modelo',
  type: 'text' as const,
  placeholder: 'ej. Hilux',
  validation: z.string().optional(),
};

export const VehiculosView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('vehiculos.crear');
  const canEdit = hasPermission('vehiculos.editar');
  const canDelete = hasPermission('vehiculos.eliminar');

  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Vehiculo;
  }>({
    isOpen: false,
    mode: 'create',
  });

  const { data, isLoading, error } = useVehiculos(filtros);
  const crearMutation = useCrearVehiculo();
  const actualizarMutation = useActualizarVehiculo();
  const eliminarMutation = useEliminarVehiculo();

  const columns = useMemo(() => [
    { key: 'codigo', title: t('common.code'), width: '120px' },
    { key: 'patente', title: 'Patente', width: '120px' },
    { key: 'tipo', title: 'Tipo', width: '130px', render: (tipo: string) => tipo || '-' },
    { key: 'marca', title: 'Marca', width: '130px', render: (marca: string) => marca || '-' },
    { key: 'modelo', title: 'Modelo', width: '130px', render: (modelo: string) => modelo || '-' },
    estadoColumn<Vehiculo>(),
  ], [t]);

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
  ], [t]);

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({ isOpen: true, mode: 'create' });
  };

  const handleEdit = (vehiculo: Vehiculo) => {
    setFormModal({ isOpen: true, mode: 'edit', data: vehiculo });
  };

  const handleDelete = async (vehiculo: Vehiculo) => {
    try {
      await eliminarMutation.mutateAsync(vehiculo.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando vehículo:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearVehiculo) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({ id: formModal.data.id, datos });
        toast.success(t('admin.update_success'));
      }
      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario vehículo:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button onClick={() => navigate('/admin')} className="hover:text-gray-700 transition-colors">
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">Vehículos</span>
      </div>

      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title="Vehículos"
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={[codigoField, patenteField, tipoField, marcaField, modeloField, estadoField]}
        title={formModal.mode === 'create' ? 'Crear vehículo' : 'Editar vehículo'}
        submitText={t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default VehiculosView;
